import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  PackageSearch, ScanBarcode, FileText, RefreshCcw, Truck, Users,
  CheckCircle2, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const POLL_MS = 3000;

const inputCls =
  "h-10 w-full rounded-md border border-[#D9E3DC] bg-white px-3 text-sm text-[#0F2A1F] focus:border-[#2DC26B] focus:outline-none focus:ring-2 focus:ring-[#2DC26B]/30";

const STATUS_LABEL = {
  CONFERENCIA: "Em Conferência",
  RECEBIDO: "Recebido",
  ENDERECADO: "Endereçado",
  CANCELADO: "Cancelado",
};

const normalizarEtiqueta = (value) => {
  const text = String(value || "").trim();
  const digits = text.replace(/\D/g, "");
  return digits.length === 14 ? digits : text;
};

const fmtDT = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "medium" });
  } catch {
    return "—";
  }
};

export default function ConferenciaVolumesPage() {
  const [recs, setRecs] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [rec, setRec] = useState(null);
  const [etiqueta, setEtiqueta] = useState("");
  const [loadingScan, setLoadingScan] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [ultimoEvento, setUltimoEvento] = useState(null);
  const [volumeNaoPrevisto, setVolumeNaoPrevisto] = useState(null);
  const inputRef = useRef(null);
  const pollingRef = useRef(null);

  const carregarLista = useCallback(async () => {
    setLoadingList(true);
    try {
      const { data } = await api.get("/recebimento");
      setRecs(data || []);
    } catch {
      toast.error("Falha ao carregar recebimentos");
    } finally {
      setLoadingList(false);
    }
  }, []);

  const carregarDetalhe = useCallback(async (rid, { silent = false } = {}) => {
    if (!rid) return;
    try {
      const { data } = await api.get(`/recebimento/${rid}`);
      setRec(data);
    } catch (e) {
      if (!silent) toast.error("Falha ao carregar conferência");
    }
  }, []);

  useEffect(() => { carregarLista(); }, [carregarLista]);

  // Polling do MongoDB para sincronizar bipagens de outros operadores
  useEffect(() => {
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    if (!selectedId) return;
    carregarDetalhe(selectedId);
    pollingRef.current = setInterval(() => carregarDetalhe(selectedId, { silent: true }), POLL_MS);
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [selectedId, carregarDetalhe]);

  const controle = rec?.controle_caixas || { total_bipado: 0, total_esperado: 0, faltantes: [], bipadas: [], esperadas: [] };
  const caixas = useMemo(() => {
    const list = (rec?.conferencia_volumes?.caixas || []).slice();
    list.sort((a, b) => (a.criada_em || "").localeCompare(b.criada_em || ""));
    return list;
  }, [rec]);

  const operadores = useMemo(() => {
    const set = new Set();
    caixas.forEach((b) => { if (b.por) set.add(b.por); });
    return Array.from(set);
  }, [caixas]);

  const percentual = controle.total_esperado > 0
    ? Math.round((controle.total_bipado / controle.total_esperado) * 100)
    : 0;

  const biparVolume = async ({ permitirNaoPrevisto = false } = {}) => {
    if (!selectedId) { toast.error("Selecione um recebimento"); return; }
    const codigo = normalizarEtiqueta(etiqueta);
    if (!codigo) { toast.error("Bipe a etiqueta da caixa"); return; }
    setLoadingScan(true);
    try {
      const { data } = await api.post(`/recebimento/${selectedId}/conferencia/caixa`, {
        etiqueta: codigo,
        permitir_nao_previsto: permitirNaoPrevisto,
      });
      setRec(data.analyze);
      setEtiqueta("");
      setVolumeNaoPrevisto(null);
      setUltimoEvento({ tipo: "ok", numero: data.caixa.numero, etiqueta: data.caixa.etiqueta, at: data.caixa.criada_em, por: data.caixa.por });
      toast.success(`Volume ${data.caixa.numero} conferido`);
      inputRef.current?.focus();
    } catch (error) {
      const status = error.response?.status;
      const rawDetail = error.response?.data?.detail;
      const detail = typeof rawDetail === "object"
        ? rawDetail.message
        : rawDetail || "Etiqueta inválida";
      if (status === 409 && rawDetail?.code === "VOLUME_NAO_PREVISTO") {
        setVolumeNaoPrevisto({ codigo, detail });
        return;
      }
      // Caso duplicidade → informar quem já conferiu
      if (status === 409) {
        const numero = codigo.length === 14 ? String(parseInt(codigo.slice(0, 5), 10)) : codigo;
        const existente = (rec?.conferencia_volumes?.caixas || []).find(
          (b) => b.numero === numero || b.etiqueta === codigo,
        );
        setUltimoEvento({
          tipo: "duplicado",
          numero,
          etiqueta: codigo,
          detail,
          at: existente?.criada_em,
          por: existente?.por,
        });
        toast.warning("Volume já conferido", { description: detail });
      } else {
        setUltimoEvento({ tipo: "erro", detail });
        toast.error(detail);
      }
    } finally {
      setLoadingScan(false);
    }
  };

  const baixarPdf = async () => {
    if (!selectedId) return;
    try {
      const response = await api.get(
        `/recebimento/${selectedId}/conferencia-volumes/relatorio.pdf`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Conferencia_NF${rec?.nf_numero || selectedId}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Erro ao gerar PDF");
    }
  };

  const opcoes = useMemo(() => {
    return recs
      .filter((r) => r.status === "CONFERENCIA" || r.status === "RECEBIDO" || r.status === "ENDERECADO")
      .map((r) => ({
        value: r.id,
        label: `NF-e ${r.nf_numero} · ${(r.fornecedor?.nome || "—").slice(0, 40)} · ${STATUS_LABEL[r.status] || r.status}`,
      }));
  }, [recs]);

  return (
    <div className="space-y-5" data-testid="conferencia-volumes-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <PackageSearch className="h-6 w-6 text-[#0F6B3A]" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-[#0F2A1F]">Conferência de Volumes</h1>
            <p className="text-xs text-[#5A6B62]">
              Bipagem colaborativa persistida no banco central — vários operadores em tempo real.
            </p>
          </div>
        </div>
        <button
          data-testid="conf-vol-refresh-btn"
          onClick={() => selectedId ? carregarDetalhe(selectedId) : carregarLista()}
          className="inline-flex items-center gap-2 rounded-md border border-[#D9E3DC] px-3 h-10 text-sm text-[#0F2A1F] hover:bg-[#F4F8F5]"
        >
          <RefreshCcw className="h-4 w-4" /> Atualizar
        </button>
      </div>

      <div className="bg-white border border-[#D9E3DC] rounded-lg shadow-sm p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Truck className="h-4 w-4 text-[#0F6B3A]" />
          <label className="text-xs font-semibold text-[#5A6B62] uppercase tracking-wide">
            Recebimento / NF-e
          </label>
        </div>
        <select
          data-testid="conf-vol-rec-select"
          value={selectedId}
          onChange={(e) => { setSelectedId(e.target.value); setUltimoEvento(null); }}
          className={inputCls}
        >
          <option value="">
            {loadingList ? "Carregando..." : opcoes.length === 0 ? "Nenhum recebimento disponível" : "Selecione um recebimento"}
          </option>
          {opcoes.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {selectedId && rec && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="conf-vol-kpis">
            <Kpi label="Total esperado" value={controle.total_esperado} color="#0F2A1F" testid="kpi-esperado" />
            <Kpi label="Conferidos" value={controle.total_bipado} color="#0F6B3A" testid="kpi-bipados" />
            <Kpi label="Pendentes" value={controle.faltantes.length} color="#B91C1C" testid="kpi-faltantes" />
            <Kpi label="Percentual" value={`${percentual}%`} color="#0F6B3A" testid="kpi-percentual" />
          </div>

          {/* Barra de progresso */}
          <div className="bg-white border border-[#D9E3DC] rounded-lg p-3 shadow-sm">
            <div className="h-2 w-full rounded-full bg-[#EAF7EF] overflow-hidden">
              <div
                className="h-full bg-[#0F6B3A] transition-all duration-300"
                style={{ width: `${Math.min(100, percentual)}%` }}
                data-testid="conf-vol-progress"
              />
            </div>
            <div className="mt-2 flex flex-wrap gap-3 items-center text-[11px] font-mono text-[#5A6B62]">
              <span className="inline-flex items-center gap-1">
                <Users className="h-3.5 w-3.5" />
                Operadores nesta conferência: <b className="text-[#0F2A1F]">{operadores.length}</b>
              </span>
              {operadores.length > 0 && (
                <span className="truncate max-w-full" data-testid="conf-vol-operadores">
                  {operadores.join(" · ")}
                </span>
              )}
              <span className="ml-auto text-[10px] tracking-wider uppercase text-[#8A9A90]">
                Sincronização automática a cada {POLL_MS / 1000}s
              </span>
            </div>
          </div>

          {/* Bipagem */}
          <div className="bg-white border border-[#D9E3DC] rounded-lg shadow-sm p-4 space-y-3">
            <div className="flex items-center gap-2">
              <ScanBarcode className="h-4 w-4 text-[#0F6B3A]" />
              <label className="text-xs font-semibold text-[#5A6B62] uppercase tracking-wide">
                Bipe a etiqueta da caixa (14 dígitos)
              </label>
            </div>
            <div className="flex gap-2 rounded-md bg-[#0B1F17] p-3">
              <input
                ref={inputRef}
                autoFocus
                data-testid="conf-vol-input"
                value={etiqueta}
                onChange={(e) => setEtiqueta(normalizarEtiqueta(e.target.value))}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); biparVolume(); } }}
                placeholder="Aponte o leitor ou digite a etiqueta..."
                className="h-12 min-w-0 flex-1 rounded-md px-3 font-mono text-base text-[#0F2A1F]"
              />
              <button
                data-testid="conf-vol-bipar-btn"
                disabled={loadingScan || !selectedId}
                onClick={biparVolume}
                className="inline-flex h-12 items-center gap-2 rounded-md bg-[#2DC26B] px-4 text-sm font-bold text-white disabled:opacity-60"
              >
                <ScanBarcode className="h-4 w-4" /> Bipar
              </button>
              <button
                data-testid="conf-vol-pdf-btn"
                onClick={baixarPdf}
                className="inline-flex h-12 items-center gap-2 rounded-md border border-[#2DC26B]/40 bg-[#164A34] px-3 text-xs font-bold text-white hover:bg-[#0F6B3A]"
                title="Baixar relatório PDF"
              >
                <FileText className="h-4 w-4" /> PDF
              </button>
            </div>

            {ultimoEvento && (
              <div
                data-testid="conf-vol-ultimo-evento"
                className={`rounded-md border p-3 text-sm ${
                  ultimoEvento.tipo === "ok"
                    ? "border-[#0F6B3A]/40 bg-[#EAF7EF] text-[#0F6B3A]"
                    : ultimoEvento.tipo === "duplicado"
                    ? "border-amber-300 bg-amber-50 text-amber-800"
                    : "border-red-200 bg-red-50 text-red-700"
                }`}
              >
                {ultimoEvento.tipo === "ok" && (
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 mt-0.5" />
                    <div>
                      <div className="font-bold">Volume {ultimoEvento.numero} registrado</div>
                      <div className="text-xs">
                        Etiqueta {ultimoEvento.etiqueta} · por <b>{ultimoEvento.por}</b> em {fmtDT(ultimoEvento.at)}
                      </div>
                    </div>
                  </div>
                )}
                {ultimoEvento.tipo === "duplicado" && (
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 mt-0.5" />
                    <div>
                      <div className="font-bold">Volume já conferido</div>
                      <div className="text-xs">
                        Código {ultimoEvento.etiqueta} — Caixa {ultimoEvento.numero}
                        {ultimoEvento.por && <> · Primeira bipagem por <b>{ultimoEvento.por}</b></>}
                        {ultimoEvento.at && <> em {fmtDT(ultimoEvento.at)}</>}
                      </div>
                      <div className="text-xs opacity-80 mt-1">{ultimoEvento.detail}</div>
                    </div>
                  </div>
                )}
                {ultimoEvento.tipo === "erro" && (
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 mt-0.5" />
                    <div>{ultimoEvento.detail}</div>
                  </div>
                )}
              </div>
            )}

            {volumeNaoPrevisto && (
              <div
                data-testid="conf-vol-nao-previsto-confirmacao"
                className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
              >
                <div className="flex items-start gap-2">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-bold">Volume fora da NF-e</div>
                    <p className="mt-1 text-xs">{volumeNaoPrevisto.detail}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        data-testid="conf-vol-nao-previsto-incluir-btn"
                        disabled={loadingScan}
                        onClick={() => biparVolume({ permitirNaoPrevisto: true })}
                        className="rounded-md bg-[#9A6B0A] px-3 py-2 text-xs font-bold text-white hover:bg-[#805807] disabled:opacity-60"
                      >
                        Incluir volume
                      </button>
                      <button
                        data-testid="conf-vol-nao-previsto-cancelar-btn"
                        disabled={loadingScan}
                        onClick={() => setVolumeNaoPrevisto(null)}
                        className="rounded-md border border-amber-300 bg-white px-3 py-2 text-xs font-bold text-amber-900 hover:bg-amber-100 disabled:opacity-60"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Histórico de bipagens */}
          <div className="bg-white border border-[#D9E3DC] rounded-lg shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-[#D9E3DC] flex items-center justify-between">
              <div>
                <h2 className="text-sm font-bold text-[#0F2A1F]">Histórico de bipagens</h2>
                <p className="text-[11px] text-[#5A6B62]">
                  NF-e {rec.nf_numero} · {caixas.length} volume(s) conferido(s) · Total esperado: {controle.total_esperado}
                </p>
              </div>
              <span className="text-[11px] font-mono text-[#5A6B62]">
                {controle.faltantes.length > 0
                  ? <>Pendentes: {controle.faltantes.slice(0, 10).join(", ")}{controle.faltantes.length > 10 ? "..." : ""}</>
                  : "Todos os volumes conferidos"}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="conf-vol-tabela">
                <thead className="bg-[#F4F8F5] text-[11px] uppercase tracking-wider text-[#5A6B62]">
                  <tr>
                    <th className="text-left px-4 py-2">Volume</th>
                    <th className="text-left px-4 py-2">Etiqueta</th>
                    <th className="text-left px-4 py-2">Colaborador</th>
                    <th className="text-left px-4 py-2">Data/Hora</th>
                    <th className="text-left px-4 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {caixas.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-[#8A9A90]" data-testid="conf-vol-vazio">
                        Nenhum volume conferido ainda para esta NF-e.
                      </td>
                    </tr>
                  )}
                  {caixas.map((b) => (
                    <tr key={`${b.numero}-${b.criada_em}`} className="border-t border-[#EEF3EF]" data-testid={`conf-vol-row-${b.numero}`}>
                      <td className="px-4 py-2 font-mono font-bold text-[#0F2A1F]">{b.numero}</td>
                      <td className="px-4 py-2 font-mono text-[#5A6B62]">{b.etiqueta || "—"}</td>
                      <td className="px-4 py-2 text-[#0F2A1F]">{b.por || "—"}</td>
                      <td className="px-4 py-2 text-[#5A6B62]">{fmtDT(b.criada_em)}</td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex px-2 py-0.5 rounded text-[11px] font-bold ${
                          b.nao_prevista
                            ? "bg-amber-100 text-amber-900"
                            : "bg-[#EAF7EF] text-[#0F6B3A]"
                        }`}>
                          {b.nao_prevista ? "Incluído manualmente" : "Conferido"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!selectedId && !loadingList && (
        <div className="bg-white border border-[#D9E3DC] rounded-lg py-16 text-center" data-testid="conf-vol-empty">
          <div className="text-[#0F2A1F] font-semibold">Selecione um recebimento para começar a conferência.</div>
          <div className="text-xs text-[#8A9A90] mt-1">
            Todas as bipagens são gravadas no banco central e compartilhadas em tempo real com outros operadores.
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, color, testid }) {
  return (
    <div className="bg-white border border-[#D9E3DC] rounded-lg shadow-sm p-3" data-testid={testid}>
      <p className="text-[10px] font-semibold text-[#5A6B62] uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-extrabold font-mono mt-1" style={{ color }}>{value}</p>
    </div>
  );
}
